import React, { FC, useState, useRef, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { TextField } from '@mui/material';
import { logEvent } from '../analytics';
import { useDisableTextSend, useInputMode, useCustomTextInput } from '../hooks';
import styles from './InputBox.module.css';
import {
    getTenantName,
    isMobile,
    isMobileBrowser,
    isUnified,
} from '../helpers';
import generalConfig from '../resources/general.json';
import Typography from '@mui/material/Typography';
import { TENANTS } from '../constants';

const SendButton: FC<{ enabled: boolean }> = ({ enabled }) => {
    return (
        <div
            id="sendButtonContainer"
            className={styles.inputBoxSendButtonContainer}
        >
            <input
                aria-label="sendButton"
                id="sendButton"
                type="submit"
                value=""
                disabled={!enabled}
                className={styles.inputBoxSendButton}
            ></input>
            <svg
                viewBox="0 0 24 24"
                className={classNames(
                    styles.inputBoxSvg,
                    enabled ? null : styles.inputBoxSvgDisabled
                )}
            >
                {getTenantName() === TENANTS.MESSAGE_ASSIST ? (
                    <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24ZM10.5207 10.5135V17.8684C10.5207 18.72 11.2175 19.4168 12.0691 19.4168C12.9207 19.4168 13.6175 18.72 13.6175 17.8684V10.5135H16.3891C17.0859 10.5135 17.4265 9.67742 16.931 9.19742L12.611 4.87742C12.3013 4.56774 11.8213 4.56774 11.5117 4.87742L7.19167 9.19742C6.71167 9.67742 7.05231 10.5135 7.74909 10.5135H10.5207Z"
                    />
                ) : (
                    <path d="M3.67124 20.4L21.1212 12.92C21.9312 12.57 21.9312 11.43 21.1212 11.08L3.67124 3.6C3.01124 3.31 2.28124 3.8 2.28124 4.51L2.27124 9.12C2.27124 9.62 2.64124 10.05 3.14124 10.11L17.2712 12L3.14124 13.88C2.64124 13.95 2.27124 14.38 2.27124 14.88L2.28124 19.49C2.28124 20.2 3.01124 20.69 3.67124 20.4Z" />
                )}
            </svg>
        </div>
    );
};

export const useInputCharacterCountLimit = (
    currentInput: string,
    inputCharacterCountLimit = 1000,
    inputRemainingCharsWarningThreshold = 100
) => {
    return useMemo(() => {
        const remainingCharacterCount =
            inputCharacterCountLimit - currentInput.length;
        return {
            remainingCharacterCount,
            isValidInputLength: remainingCharacterCount >= 0,
            remainingCharactersThreshold: inputRemainingCharsWarningThreshold,
            trimToLimit: (val: string) =>
                val.slice(0, inputCharacterCountLimit),
        };
    }, [currentInput]);
};

const RemainingCharactersWarning: FC<{
    remainingCharacterCount: number;
    remainingCharactersThreshold: number;
}> = ({ remainingCharacterCount, remainingCharactersThreshold }) => {
    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'flex-end',
            }}
        >
            {remainingCharacterCount <= remainingCharactersThreshold && (
                <Typography
                    variant="body2"
                    sx={(theme) => ({
                        color:
                            remainingCharacterCount > 0
                                ? theme.palette.grey['700']
                                : theme.palette.error.main,
                        margin: '0 16px 0 0',
                    })}
                >
                    {remainingCharacterCount} characters remaining
                </Typography>
            )}
        </div>
    );
};

export const InputBox: FC<{
    sendMessage: (message: string) => void;
    focusCounter?: number | undefined;
    inputCharacterCountLimit?: number | undefined;
    inputRemainingCharsWarningThreshold?: number | undefined;
    isMenu?: boolean;
    shouldLogAnalyticsEvents: boolean;
}> = ({
    sendMessage,
    focusCounter,
    isMenu,
    shouldLogAnalyticsEvents,
    inputCharacterCountLimit,
    inputRemainingCharsWarningThreshold,
}) => {
    const { disableTextSend } = useDisableTextSend();
    const { customTextInput, setCustomTextInput } = useCustomTextInput();
    const { inputMode } = useInputMode();

    const [userInput, setUserInput] = useState('');
    const {
        remainingCharacterCount,
        isValidInputLength,
        remainingCharactersThreshold,
        trimToLimit,
    } = useInputCharacterCountLimit(
        userInput,
        inputCharacterCountLimit,
        inputRemainingCharsWarningThreshold
    );
    const [textInputClicked, setTextInputClicked] = useState(false);

    const validateUserInput = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        if (customTextInput === 'zip') {
            let text = e.target.value.replace(/\D/g, '');
            if (text.length > 5) {
                text = text.substring(0, 5);
            }
            setUserInput(text);
        } else {
            const text = trimToLimit(e.target.value);
            setUserInput(text);
        }
    };

    const clearInputBar: () => void = () => {
        setUserInput('');
    };

    const handleTextInputOnClick = () => {
        const loggingConditions = [
            shouldLogAnalyticsEvents,
            getTenantName() === TENANTS.MESSAGE_ASSIST,
            !textInputClicked,
        ];
        const shouldLog = loggingConditions.every(Boolean);
        if (shouldLog) {
            setTextInputClicked(true);
            logEvent(`Message Provider - New message - Input Message Click`);
        }
    };

    const handleSubmit: (event: any) => void = (event) => {
        event.preventDefault();
        if (!inputEnabled) {
            return;
        }

        sendMessage(userInput.trim());
        setCustomTextInput('default');
        clearInputBar();

        if (shouldLogAnalyticsEvents) {
            if (customTextInput === 'zip') {
                logEvent('ZipCode Entered');
            } else {
                logEvent('User typed utterance');
            }
        }
    };

    const placeHolder: () => string = () => {
        switch (customTextInput) {
            case 'zip':
                return generalConfig.providence.inputZipPlaceholder;
            case 'symptom':
                return generalConfig.providence.inputSymptomPlaceholder;
            default:
                if (isMenu) {
                    return generalConfig.providence.inputMenuPlaceholder;
                }
                return generalConfig.providence.inputPlaceholder;
        }
    };

    const inputElement = useRef(null);
    if (!isMobile() && !isMobileBrowser() && !isUnified()) {
        useEffect(() => {
            if (inputElement.current) {
                (inputElement.current as any).focus();
            }
        }, [focusCounter]);
    }

    const invalidZip = customTextInput === 'zip' && userInput.length !== 5;
    const inputEnabled =
        !disableTextSend &&
        userInput.trim() !== '' &&
        !invalidZip &&
        isValidInputLength;

    useEffect(() => {
        const loggingConditions = [
            shouldLogAnalyticsEvents,
            getTenantName() === TENANTS.MESSAGE_ASSIST,
        ];
        const shouldLog = loggingConditions.every(Boolean);
        const visibility = inputMode === 'enabled' ? 'Shown' : 'Hidden';

        if (shouldLog) {
            logEvent(
                `Message Provider - New message - Input Message ${visibility}`
            );
        }
    }, [inputMode]);

    return inputMode === 'enabled' ? (
        <>
            <RemainingCharactersWarning
                remainingCharacterCount={remainingCharacterCount}
                remainingCharactersThreshold={remainingCharactersThreshold}
            />
            <div
                className={classNames(
                    styles.inputBoxContainer,
                    styles[getTenantName()]
                )}
            >
                <form
                    id="inputForm"
                    className={classNames(
                        styles.inputBox,
                        styles[getTenantName()],
                        disableTextSend ? styles.inputBoxDisabled : null
                    )}
                    onSubmit={handleSubmit}
                >
                    <TextField
                        autoComplete="off"
                        variant="standard"
                        className={styles.inputBoxTextArea}
                        inputRef={inputElement}
                        inputProps={{
                            // these are custom props to be passed to the <input> component
                            'aria-label': 'inputBox',
                        }}
                        InputProps={{
                            // these are pre-defined props for this Material UI component
                            disableUnderline: true,
                        }}
                        disabled={disableTextSend}
                        placeholder={placeHolder()}
                        onChange={(
                            e: React.ChangeEvent<
                                HTMLInputElement | HTMLTextAreaElement
                            >
                        ) => validateUserInput(e)}
                        value={userInput}
                        onClick={() => handleTextInputOnClick()}
                    />
                    <div>
                        <SendButton enabled={inputEnabled} />
                    </div>
                </form>
            </div>
        </>
    ) : (
        <></>
    );
};
